import React from "react";
import { motion } from "framer-motion";
import { Eye, Heart } from "lucide-react";
import { resolveImage } from "../lib/api";
import { useLiked } from "../lib/likes";

export default function ProjectCard({ project, index = 0, onOpen }) {
  const liked = useLiked(project.id);
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.65, delay: (index % 6) * 0.07, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onOpen(project)}
      data-testid={`project-card-${project.id}`}
      className="card-lift group relative flex flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white text-left hover:-translate-y-1.5 hover:border-[#2563EB]/50"
    >
      <div className="clipped-frame relative h-52 overflow-hidden">
        <img
          src={resolveImage(project.image_url)}
          alt={project.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />
        {project.featured && (
          <span className="absolute left-4 top-4 rounded-full bg-[#2563EB] px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white shadow-[0_4px_16px_rgba(37,99,235,0.5)]">
            Featured
          </span>
        )}
        <span className="absolute bottom-3 right-4 rounded-full border border-white/30 bg-white/85 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-700 backdrop-blur-md">
          {project.category_label || project.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2563EB]">{project.department_label || project.department}</p>
        <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-slate-900 transition-colors duration-300 group-hover:text-[#1E3A8A]">
          {project.title}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-500 line-clamp-2">{project.tagline}</p>
        <div className="mt-5 flex items-center gap-5 border-t border-slate-100 pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
          <span className="flex items-center gap-1.5" data-testid={`project-views-${project.id}`}>
            <Eye size={13} className="text-[#2563EB]" /> {project.views.toLocaleString()}
          </span>
          <span className="flex items-center gap-1.5" data-testid={`project-likes-${project.id}`}>
            <Heart size={13} className={liked ? "text-rose-500" : "text-rose-400"} fill={liked ? "currentColor" : "none"} /> {project.likes.toLocaleString()}
          </span>
          <span className="ml-auto text-slate-300 transition-colors group-hover:text-[#2563EB]">→</span>
        </div>
      </div>
    </motion.button>
  );
}
